import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Dispatch, useEffect, useMemo } from "react";

import Field from "@/components/field";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";
import { safeCalcItem } from "@buildpy/core/modules/quote/safe-calc";
import Money from "@/components/money";
import { CostField, MarginField, MoneyField, QtyField } from "@/modules/quote/ui/editor/item-row";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import PhoneSheet from "@/modules/quote/ui/editor/mobile/phone-sheet";
import SaleOverrideNote from "@/modules/quote/ui/editor/sale-override-note";
import { EditorAction, EditorItem, EditorItemPatch, EditorTrade, ItemRef } from "@buildpy/core/modules/quote/editor/types";
import UnitCell from "@/modules/quote/ui/editor/unit-cell";
import { lockedNow, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";

// 폰 시트 칸 — outline 칸을 44px·16px 글씨(iOS 확대 방지)로 키운다.
const PHONE_CELL = "min-h-11 text-lg";

export interface FlatRow {
  item: EditorItem;
  parentKey: string | null;
}

// 공종의 줄을 화면 순서(부모 → 그 하위)로 편다 — 이전·다음 이동과 「3 / 6」 의 기준.
export const flatRowsOf = (trade: EditorTrade): FlatRow[] =>
  trade.items.flatMap((item) => [
    { item, parentKey: null },
    ...item.children.map((child) => ({ item: child, parentKey: item.key })),
  ]);

// 새로 넣은 빈 줄로 열리면 작업내역부터 치게 한다 — 있는 줄은 키보드를 띄우지 않는다.
export const focusItemOnOpen = (item: Pick<EditorItem, "item">): boolean => item.item.trim() === "";

interface Props {
  trade: EditorTrade | undefined;
  target: ItemRef | null;
  area: number | null;
  areaM2?: number | null;
  simple: boolean;
  setSimple: (next: boolean) => void;
  spaces: { code: string; label: string }[];
  dispatch: Dispatch<EditorAction>;
  onSuggest: (ref: ItemRef) => void;
  onSelect: (ref: ItemRef | null) => void;
  onAddChild: (tradeKey: string, parentKey: string) => void;
  // 원가·마진 칸을 보일지 · 마진은 vatMode 기준.
  showCost?: boolean;
  vatMode?: VatMode;
  // 잠긴 견적 — 칸·단추를 끈다(이전·다음 이동과 단가 입력 방식 전환은 그대로).
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — fieldset 이 꺼지며 나는 blur 복원·늦은 확정을 버린다.
  isLocked: () => boolean;
}

// 항목 한 줄의 모든 칸을 고치는 바닥 시트 — 카드 보기와 같은 액션(SET_ITEM·SET_SIMPLE_PRICE·MOVE_ITEM·ADD_CHILD·REMOVE_ITEM)만 보낸다.
const MobileItemSheet = ({
  trade,
  target,
  area,
  areaM2,
  simple,
  setSimple,
  spaces,
  dispatch: rawDispatch,
  onSuggest,
  onSelect,
  onAddChild,
  showCost = false,
  vatMode = "INCL",
  disabled = false,
  isLocked,
}: Props) => {
  // 시트 칸이 보내는 액션은 보내는 순간 잠겨 있으면 버린다.
  const dispatch = useMemo(() => unlessLocked(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  const rows = useMemo(() => (trade ? flatRowsOf(trade) : []), [trade]);
  const index = target ? rows.findIndex((r) => r.item.key === target.itemKey) : -1;
  const row = index >= 0 ? rows[index] : undefined;

  // 되돌리기·삭제로 줄이 사라지면 시트를 닫는다 — 다시 살아날 때 저절로 열리지 않게 고른 줄도 비운다.
  useEffect(() => {
    if (target && !row) onSelect(null);
  }, [target, row, onSelect]);

  const spaceOptions = useMemo(
    () => [{ value: "", label: "—" }, ...spaces.map((space) => ({ value: space.code, label: space.label }))],
    [spaces]
  );

  if (!trade || !row) return null;

  const { item, parentKey } = row;
  const ref: ItemRef = { tradeKey: trade.key, itemKey: item.key, parentKey };
  const isSet = item.children.length > 0;
  const siblings = parentKey ? (trade.items.find((i) => i.key === parentKey)?.children ?? []) : trade.items;
  const pos = siblings.findIndex((i) => i.key === item.key);
  const amounts = safeCalcItem(item);
  const patch = (next: EditorItemPatch) => dispatch({ type: "SET_ITEM", ref, patch: next });
  const settle = () => onSuggest(ref);
  const go = (to: number) => {
    const next = rows[to];
    if (next) onSelect({ tradeKey: trade.key, itemKey: next.item.key, parentKey: next.parentKey });
  };

  return (
    <PhoneSheet
      caption={`${trade.name} · ${index + 1} / ${rows.length}`}
      description="작업내역·수량·단가를 고칩니다. 고친 값은 바로 반영됩니다"
      open
      title="항목 편집"
      actions={
        <>
          <Button aria-label="이전 항목" className="shrink-0" disabled={index <= 0} size="icon" variant="ghost" onClick={() => go(index - 1)}>
            <ChevronUp />
          </Button>
          <Button
            aria-label="다음 항목"
            className="shrink-0"
            disabled={index >= rows.length - 1}
            size="icon"
            variant="ghost"
            onClick={() => go(index + 1)}>
            <ChevronDown />
          </Button>
        </>
      }
      footer={
        <>
          <Button
            disabled={disabled}
            variant="destructive"
            onClick={() => {
              dispatch({ type: "REMOVE_ITEM", ref });
              onSelect(null);
            }}>
            삭제
          </Button>
          <Button className="flex-1" variant="primary" onClick={() => onSelect(null)}>
            완료
          </Button>
        </>
      }
      onOpenChange={(open) => {
        if (!open) onSelect(null);
      }}>
      {/* 줄이 바뀌면 칸 안의 입력 중 상태(수량·단가 검증)를 새로 시작한다. */}
      <div key={item.key} className="flex flex-col gap-y-3.5" data-testid="phone-item-fields">
        <fieldset aria-describedby={lockedDescribedBy(disabled)} className="flex min-w-0 flex-col gap-y-3.5" disabled={disabled}>
        <Field label="작업내역">
          <Textarea
            autoFocus={focusItemOnOpen(item)}
            className="min-h-[4.25rem] text-lg"
            disabled={disabled}
            placeholder="작업내역"
            value={item.item}
            onBlur={settle}
            onChange={(e) => patch({ item: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="공간">
            <SimpleSelect
              aria-label="공간"
              disabled={disabled}
              options={spaceOptions}
              size="lg"
              value={item.space_code}
              onValueChange={(next) => patch({ space_code: next })}
            />
          </Field>
          <Field label="범위">
            <Input disabled={disabled} inputSize="lg" name="scope" placeholder="예: 벽·천장" value={item.scope} onChange={(e) => patch({ scope: e.target.value })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-y-1">
            <span className="text-xs text-muted-foreground">수량</span>
            <QtyField area={area} areaM2={areaM2} className={PHONE_CELL} disabled={isSet || disabled} item={item} variant="outline" onCommit={patch} />
          </div>
          <div className="flex flex-col gap-y-1">
            <span className="text-xs text-muted-foreground">규격</span>
            <div className="flex min-h-11 items-center rounded-md border border-input bg-background px-1 [&_button]:text-lg [&_input]:text-lg">
              <UnitCell disabled={isSet || disabled} value={item.unit} onChange={(unit) => patch({ unit })} onSettled={settle} />
            </div>
          </div>
        </div>
        </fieldset>

        <div className="flex flex-col gap-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground" id="phone-item-price-label">
              단가
            </span>
            <div aria-labelledby="phone-item-price-label" className="flex rounded-md bg-muted p-0.5" role="group">
              <Button aria-pressed={simple} size="sm" variant={simple ? "secondary" : "ghost"} onClick={() => setSimple(true)}>
                간단
              </Button>
              <Button aria-pressed={!simple} size="sm" variant={simple ? "ghost" : "secondary"} onClick={() => setSimple(false)}>
                자재·노무
              </Button>
            </div>
          </div>
          <fieldset aria-describedby={lockedDescribedBy(disabled)} className="min-w-0" disabled={disabled}>
          {simple ? (
            <MoneyField
              ariaLabel="단가"
              className={PHONE_CELL}
              variant="outline"
              disabled={isSet || disabled}
              item={item}
              restore={{ material_unit_price: item.material_unit_price, labor_unit_price: item.labor_unit_price, suggested: item.suggested }}
              value={item.material_unit_price + item.labor_unit_price}
              onCommit={(value) => dispatch({ type: "SET_SIMPLE_PRICE", ref, value })}
              onRestore={patch}
            />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <MoneyField
                ariaLabel="자재 단가"
                className={PHONE_CELL}
                variant="outline"
                disabled={isSet || disabled}
                item={item}
                restore={{ material_unit_price: item.material_unit_price, suggested: item.suggested }}
                value={item.material_unit_price}
                onCommit={(material_unit_price) => patch({ material_unit_price })}
                onRestore={patch}
              />
              <MoneyField
                ariaLabel="노무 단가"
                className={PHONE_CELL}
                variant="outline"
                disabled={isSet || disabled}
                item={item}
                restore={{ labor_unit_price: item.labor_unit_price, suggested: item.suggested }}
                value={item.labor_unit_price}
                onCommit={(labor_unit_price) => patch({ labor_unit_price })}
                onRestore={patch}
              />
            </div>
          )}
          </fieldset>
          {isSet && <span className="text-xs text-muted-foreground">세트는 하위 항목 합계로 계산합니다</span>}
          {!isSet && item.suggested && <span className="text-xs text-status-new">단가표에서 채운 제안값입니다</span>}
        </div>

        <fieldset aria-describedby={lockedDescribedBy(disabled)} className="flex min-w-0 flex-col gap-y-3.5" disabled={disabled}>
        {showCost && (
          <div className="grid grid-cols-2 gap-2" data-testid="phone-item-cost">
            <div className="flex min-w-0 flex-col gap-y-1.5">
              <span className="text-xs text-muted-foreground">원가 (부가세 별도)</span>
              <CostField
                className={PHONE_CELL}
                disabled={isSet}
                locked={disabled}
                item={item}
                variant="outline"
                onCommit={(value) => dispatch({ type: "SET_COST", ref, value })}
                onRestore={patch}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-y-1.5">
              <span className="text-xs text-muted-foreground">마진 (%)</span>
              <MarginField
                className={PHONE_CELL}
                disabled={disabled}
                item={item}
                variant="outline"
                vatMode={vatMode}
                onCommit={(margin) => dispatch({ type: "SET_MARGIN", ref, margin })}
                onRestore={patch}
              />
            </div>
            <SaleOverrideNote
              className="col-span-2"
              item={item}
              vatMode={vatMode}
              onReset={() => dispatch({ type: "RESET_TO_CALC", ref })}
            />
          </div>
        )}

        <dl className="flex flex-col gap-y-1 rounded-md bg-background p-3 text-sm" data-testid="phone-item-preview">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">자재 금액</dt>
            <dd><Money value={amounts.material_amount} /></dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">노무 금액</dt>
            <dd><Money value={amounts.labor_amount} /></dd>
          </div>
          <div className="flex justify-between font-semibold">
            <dt>합계</dt>
            <dd><Money value={amounts.amount} /></dd>
          </div>
        </dl>

        <Field label="비고">
          <Input disabled={disabled} inputSize="lg" name="note" value={item.note} onChange={(e) => patch({ note: e.target.value })} />
        </Field>

        <div className="flex flex-wrap gap-2">
          {parentKey === null && (
            <Button disabled={disabled} size="sm" variant="outline" onClick={unlessLocked(lockedNow(disabled, isLocked), () => onAddChild(trade.key, item.key))}>
              <Plus /> 세트 하위 추가
            </Button>
          )}
          <Button disabled={disabled || pos <= 0} size="sm" variant="outline" onClick={() => dispatch({ type: "MOVE_ITEM", ref, dir: -1 })}>
            <ChevronUp /> 위로
          </Button>
          <Button
            disabled={disabled || pos < 0 || pos >= siblings.length - 1}
            size="sm"
            variant="outline"
            onClick={() => dispatch({ type: "MOVE_ITEM", ref, dir: 1 })}>
            <ChevronDown /> 아래로
          </Button>
        </div>
        </fieldset>
      </div>
    </PhoneSheet>
  );
};

export default MobileItemSheet;
